import { Card } from 'components/Card';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL_BACK } from 'store/actions';
import { eventSetupEngageCheckUpdated } from 'store/remote/actions';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { cloneDeep, isEqual } from 'lodash';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { CardRow } from 'components/CardRow';
import { smartText } from 'lib/str';

export const EngageSettingsCheckModal: React.FC<
  ModalScreenProps<ModalScreens.SettingsEngageCheck>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const confirmUnsaved = useConfirmUnsaved();

  const { eventKey, check } = route.params;

  const dataRef = React.useRef(cloneDeep(check));
  const isDirty = React.useCallback(
    () => !isEqual(check, dataRef.current),
    [check],
  );
  const isDirtyRef = React.useRef<boolean>(false);
  isDirtyRef.current = isDirty();

  const handleSave = React.useCallback(() => {
    dispatch(
      eventSetupEngageCheckUpdated({ eventKey, check: dataRef.current }),
    );
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch, eventKey]);

  const handleClose = React.useCallback(() => {
    if (isDirty()) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, dispatch, handleSave, isDirty]);

  const handleChangeValue = <K extends keyof typeof dataRef.current>(
    key: K,
    value: (typeof dataRef.current)[K],
    doForceUpdate: boolean = false,
  ) => {
    dataRef.current[key] = value;
    if (doForceUpdate || isDirtyRef.current !== isDirty()) {
      forceUpdate();
    }
  };

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirty() });
  }, [navigation, isDirty]);
  useEsc(handleClose);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading="Engage Check"
          subheading={check.name}
          cancelButtonText="Close"
          onPressCancelButton={handleClose}
          onPressSaveButton={handleSave}
        />
      }
    >
      <Card
        explanation={smartText(
          'This check is shown on the "Asset" and "Element" pages',
        )}
      >
        <CardRow
          label="Enable Check"
          checkedValue={dataRef.current.enable}
          onPress={() =>
            handleChangeValue('enable', !dataRef.current.enable, true)
          }
        />
      </Card>

      <Card label="Description">
        <TextAreaField
          placeholder="No Description"
          onChange={(value) => handleChangeValue('description', value)}
          value={dataRef.current.description}
          onEsc={handleClose}
        />
      </Card>
    </PageScrollView>
  );
};
